import { usePageTitle } from "@/hooks/usePageTitle";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2, CalendarDays, Users, ClipboardList, Utensils, BookOpen, Lock, PhoneCall } from "lucide-react";

const IC_LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_inbody-powered/artifacts/371pmp6w_Inner%20Circle%20Logo.png";
const CALENDLY_URL = "https://calendly.com/andreawood120/discovery-call?month=2026-09";
const GOLD = "#C9A227";

const INCLUDED = [
  { icon: CalendarDays, text: "8 weekly 1-1 sessions (30 minutes each) with Andrea Wood, Nutritional Therapist (17 years' experience)" },
  { icon: ClipboardList, text: "Structured progress review every session — your food diary, your challenges, your wins — so nothing slips through the cracks" },
  { icon: Check, text: "Tailored guidance across the 4 Pillars (Insulin & Blood Sugar Mastery, Protein Advantage, 80/20, Gut Health & Circadian Rhythms), adapted to what you need most each week — whether that's stress management, fuelling for training and recovery, or gut health" },
  { icon: Utensils, text: "Ongoing menu plan support throughout the course" },
  { icon: BookOpen, text: "Full access to the MBBC virtual platform and content library for the duration of your 8 weeks" },
  { icon: Users, text: "Maximum 6 people — this is genuinely private, one-to-one support, not a group programme" },
];

export default function InnerCircle() {
  usePageTitle("MBBC Inner Circle — 8-Week 1-1 Course");
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/inner-circle/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ sold_out: false, price: 449, spots_left: 6 }));
  }, []);

  const reserve = async () => {
    if (!user && !email) { toast.error("Please enter your email to continue."); return; }
    if (!terms) { toast.error("Please accept the Terms & Conditions to continue."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan: "inner_circle",
        origin_url: window.location.origin,
        email: user ? undefined : email,
        terms_accepted: terms,
      });
      window.location.href = data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" && d.length < 160 && !d.includes("<") ? d : "Could not start checkout. Please try again shortly.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 pt-28 pb-24" data-testid="inner-circle-page">
        {/* hero */}
        <div className="bg-navy rounded-2xl p-8 sm:p-12 text-white relative overflow-hidden">
          <div className="brand-blob" style={{ width: 320, height: 320, top: -90, right: -70, background: GOLD, opacity: 0.25 }} />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD }}>
                <Lock className="w-4 h-4" /> Only 6 spots available
              </span>
              <h1 className="mt-3 font-heading text-4xl sm:text-5xl tracking-tight">MBBC Inner Circle</h1>
              <p className="mt-4 text-xl text-white/85 font-heading">A Private, Guided 8-Week Journey Through the 4 Pillars</p>
              {status?.sold_out && (
                <p className="mt-4 inline-block text-sm font-semibold rounded-full px-4 py-1.5 bg-white/10 text-white/80" data-testid="sold-out-badge">
                  Sold out — register your interest for the next intake
                </p>
              )}
            </div>
            <img src={IC_LOGO} alt="MBBC Inner Circle" className="hidden md:block w-52 h-auto rounded-2xl bg-white p-3" />
          </div>
        </div>

        {/* is this you */}
        <div className="mt-14 max-w-3xl">
          <h2 className="font-heading text-2xl sm:text-3xl text-navy">Is This You?</h2>
          <p className="mt-4 text-[#57534E] leading-relaxed">
            You've read the research, tried the diets, and maybe even joined a slimming club — but sometimes
            you need more than a handbook. You need someone reviewing your progress, week by week, and helping
            you apply it to your actual life.
          </p>
          <p className="mt-3 text-[#57534E] leading-relaxed font-medium">
            If you're ready for genuine accountability and a bespoke plan built around you, Inner Circle is for you.
          </p>
        </div>

        {/* what's included */}
        <div className="mt-14">
          <h2 className="font-heading text-2xl sm:text-3xl text-navy">What's Included</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            {INCLUDED.map((f, i) => (
              <div key={i} className="flex items-start gap-4 bg-white border border-[#E7E5E4] rounded-2xl p-5 shadow-sm">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(201,162,39,0.12)", color: GOLD }}>
                  <f.icon className="w-5 h-5" />
                </div>
                <p className="text-sm text-[#44403C] leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* how it works */}
        <div className="mt-14 bg-white border border-[#E7E5E4] rounded-2xl p-8 sm:p-10 shadow-sm">
          <h2 className="font-heading text-2xl sm:text-3xl text-navy">How It Works</h2>
          <p className="mt-4 text-[#57534E] leading-relaxed">
            Each 30-minute session starts with a review of your food diary and progress, addressing whatever
            challenges have come up that week — meal prep, planning, motivation, life getting in the way. The
            rest of the time is yours: a deeper dive into whichever of the 4 Pillars matters most for you right now.
          </p>
          <p className="mt-3 text-[#57534E] leading-relaxed">
            Week by week, we build a picture of what's really going on for your body — and adjust as we go.
          </p>
          <p className="mt-4 text-[#44403C] leading-relaxed font-medium" data-testid="how-it-works-cta-text">
            Reserve your place below, or{" "}
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="underline font-semibold" style={{ color: "#B8912A" }}>
              book a free 15 minute discovery call
            </a>{" "}
            with Andrea to discuss how the Inner Circle can help you.
          </p>
        </div>

        {/* pricing / reserve */}
        <div className="mt-14 rounded-2xl p-8 sm:p-10 text-white relative overflow-hidden bg-navy" data-testid="inner-circle-pricing" style={{ border: `2px solid ${GOLD}` }}>
          <div className="brand-blob" style={{ width: 260, height: 260, bottom: -80, left: -60, background: GOLD, opacity: 0.2 }} />
          <div className="relative">
            <h2 className="font-heading text-2xl sm:text-3xl">What It Costs</h2>
            {status?.sold_out ? (
              <div className="mt-6" data-testid="sold-out-block">
                <p className="font-heading text-2xl" style={{ color: "#E8CB6B" }}>Sold out</p>
                <p className="mt-2 text-white/70 text-sm">All 6 spots have been taken. Want to hear when the next intake opens? <Link to="/faq" className="underline">Get in touch</Link> or join as a Virtual member in the meantime.</p>
                <Link to="/join" className="mt-6 inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-full px-7 py-3 text-sm font-semibold">Explore Virtual membership <ArrowRight className="w-4 h-4" /></Link>
              </div>
            ) : (
              <>
                <div className="mt-5 flex flex-wrap items-end gap-4">
                  <span className="font-heading text-5xl" style={{ color: "#E8CB6B" }} data-testid="inner-circle-price">£{status ? Math.round(status.price) : 449}</span>
                </div>
                <p className="mt-3 text-sm text-white/70">
                  One-off payment · With only 6 spots available, places are limited to keep
                  this genuinely personal.
                </p>
                <div className="mt-7 max-w-md space-y-4">
                  {!user && (
                    <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Your email address"
                      data-testid="inner-circle-email"
                      className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:border-teal" />
                  )}
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input data-testid="inner-circle-terms" type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded accent-[#0d9488]" />
                    <span className="text-xs text-white/70">
                      I agree to the <Link to="/terms" target="_blank" className="underline text-white">Terms &amp; Conditions</Link> and{" "}
                      <Link to="/privacy" target="_blank" className="underline text-white">Privacy Policy</Link>, and I request
                      immediate access to member content — I understand this waives my 14-day cooling-off right once I access any Content.
                    </span>
                  </label>
                  <button onClick={reserve} disabled={busy || !terms} data-testid="inner-circle-reserve"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-navy transition-opacity disabled:opacity-50"
                    style={{ background: "#E8CB6B" }}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Reserve Your Spot <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="mt-8 text-sm text-[#78716C] max-w-3xl">
          This course focuses specifically on body composition and metabolic health. If you have other health
          concerns you'd like support with, a separate full 1-1 consultation would be a better fit —{" "}
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="discovery-call-inline-link" className="text-teal font-medium underline">book a 15 minute discovery call</a> to discuss.
        </p>

        {/* discovery call */}
        <div className="mt-10 bg-white border-2 rounded-2xl p-8 sm:p-10 shadow-sm flex flex-wrap items-center justify-between gap-6" style={{ borderColor: "#C9A227" }} data-testid="discovery-call-block">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl text-navy">Not sure yet? Book a discovery call</h2>
            <p className="mt-3 text-[#57534E] leading-relaxed">
              If you have any questions about the course, or if you have health concerns beyond body composition
              and need to know if we cover this, <strong>'book a discovery call'</strong> is your next step.
              Let's discuss how we can help you best.
            </p>
          </div>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="discovery-call-cta"
            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-navy hover:opacity-90 transition-opacity"
            style={{ background: "#E8CB6B" }}>
            <PhoneCall className="w-4 h-4" /> Book a 15-minute discovery call
          </a>
        </div>

        {/* then what */}
        <div className="mt-12 max-w-3xl">
          <h2 className="font-heading text-2xl text-navy">Then What?</h2>
          <p className="mt-3 text-[#57534E] leading-relaxed">
            Your platform access is part of your 8-week journey — once the course ends, you're welcome to continue
            your progress by rejoining MBBC as a virtual member, or converting to the physical hybrid model when
            classes open.
          </p>
          <p className="mt-6 text-sm text-[#78716C] italic">
            Andrea Wood is a Nutritional Therapist with 17 years' experience helping women navigate body
            composition, hormonal balance, and long-term health.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
